import React, { ReactElement, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';

import { Timestamp } from 'firebase/firestore';

import { NavigateOptions, useLocation } from 'react-router-dom';

import SvgIcon from '@mui/material/SvgIcon';

import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import EventIcon from '@mui/icons-material/Event';
import GolfCourseIcon from '@mui/icons-material/GolfCourse';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import HomeIcon from '@mui/icons-material/Home';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PersonIcon from '@mui/icons-material/Person';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import SportsGolfIcon from '@mui/icons-material/SportsGolf';

import GratiIcon from '../components/common/GratiIcon';
import { NoticeErrorLevel } from '../types/Notice';
import { SaveStatus, SAVE_STATUSES } from '../types/SaveStatus';

export interface NavigateToProps {
  to: string;
  options?: NavigateOptions;
}

export interface GratiNotice {
  title: string;
  message: string;
  displayAt: Timestamp;
  expiresAt: Timestamp;
  severity: NoticeErrorLevel;
}

export interface ActionProps {
  icon?: typeof SvgIcon;
  iconElement?: ReactNode;
  name: string;
  handleClick?: () => void;
  variant?: 'icon' | 'text' | 'outlined' | 'contained';
  type?: 'submit' | 'button';
  form?: string;
  disabled?: boolean;
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' | 'inherit';
}

export type { SaveStatus };
export { SAVE_STATUSES };

export interface PageContextProps {
  name: string;
  icon?: typeof SvgIcon;
  to: string;
  isHomePage: boolean;
  showNavRail?: boolean;
  showLogin: boolean;
  showLogout: boolean;
  showMembers: boolean;
  showAdminPro: boolean;
  showGod: boolean;
  showIfOneOrg: boolean;
  showIfOneLeague: boolean;
  actions?: ActionProps[];
  fab?: ActionProps;
}

const defaultPageContexts: PageContextProps[] = [
  { name: 'Home', icon: HomeIcon, to: '/', isHomePage: true, showNavRail: true, showLogin: true, showLogout: false, showMembers: true, showAdminPro: true, showGod: true, showIfOneOrg: true, showIfOneLeague: true },
  { name: 'Events', icon: EventIcon, to: '/events', isHomePage: false, showNavRail: true, showLogin: true, showLogout: false, showMembers: true, showAdminPro: true, showGod: true, showIfOneOrg: true, showIfOneLeague: true },
  { name: 'Members', icon: PersonIcon, to: '/members', isHomePage: false, showNavRail: true, showLogin: true, showLogout: false, showMembers: true, showAdminPro: true, showGod: true, showIfOneOrg: true, showIfOneLeague: true },
  { name: 'Guests', icon: PersonOutlinedIcon, to: '/guests', isHomePage: false, showNavRail: true, showLogin: true, showLogout: false, showMembers: false, showAdminPro: false, showGod: true, showIfOneOrg: true, showIfOneLeague: true },
  { name: 'Requests', icon: GroupAddIcon, to: '/requests', isHomePage: false, showNavRail: true, showLogin: true, showLogout: false, showMembers: false, showAdminPro: false, showGod: true, showIfOneOrg: true, showIfOneLeague: true },
  { name: 'Orgs', icon: SportsGolfIcon, to: '/orgs', isHomePage: false, showNavRail: true, showLogin: true, showLogout: false, showMembers: true, showAdminPro: true, showGod: true, showIfOneOrg: false, showIfOneLeague: false },
  { name: 'Leagues', icon: EmojiEventsIcon, to: '/leagues', isHomePage: false, showNavRail: true, showLogin: true, showLogout: false, showMembers: true, showAdminPro: true, showGod: true, showIfOneOrg: true, showIfOneLeague: false },
  { name: 'Courses', icon: GolfCourseIcon, to: '/courses', isHomePage: false, showNavRail: true, showLogin: true, showLogout: false, showMembers: true, showAdminPro: true, showGod: true, showIfOneOrg: true, showIfOneLeague: true },
  { name: 'Docs', icon: LibraryBooksIcon, to: '/handbooks', isHomePage: false, showNavRail: true, showLogin: true, showLogout: false, showMembers: true, showAdminPro: true, showGod: true, showIfOneOrg: true, showIfOneLeague: true },
  { name: 'Notices', icon: NotificationsIcon, to: '/notices', isHomePage: false, showNavRail: true, showLogin: true, showLogout: false, showMembers: false, showAdminPro: true, showGod: true, showIfOneOrg: true, showIfOneLeague: true },
  { name: "", to: "/course", showLogin: false, showLogout: false, isHomePage: false, showMembers: true, showAdminPro: false, showGod: true, showIfOneOrg: true, showIfOneLeague: true },
  { name: "", to: "/event", showLogin: false, showLogout: false, isHomePage: false, showMembers: true, showAdminPro: false, showGod: true, showIfOneOrg: true, showIfOneLeague: true },
  { name: "", to: "/org", showLogin: false, showLogout: false, isHomePage: false, showMembers: true, showAdminPro: false, showGod: true, showIfOneOrg: true, showIfOneLeague: true },
  { name: "", to: "/league", showLogin: false, showLogout: false, isHomePage: false, showMembers: true, showAdminPro: false, showGod: true, showIfOneOrg: true, showIfOneLeague: true },
  { name: "", to: "/profile", showLogin: false, showLogout: false, isHomePage: false, showMembers: true, showAdminPro: false, showGod: true, showIfOneOrg: true, showIfOneLeague: true },
  { name: "", to: "/welcome", showLogin: false, showLogout: false, isHomePage: true, showMembers: true, showAdminPro: true, showGod: true, showIfOneOrg: true, showIfOneLeague: true },
];

export interface AppProviderProps {
  children?: ReactNode
}

export interface AppContextModel {
  actions: ActionProps[];
  pageActions: ActionProps[];
  appTitle: string;
  getPageTitle: () => string;
  isHomePage: boolean;
  notices: GratiNotice[];
  pageIcon: typeof SvgIcon;
  pageContexts: PageContextProps[];
  previousLocation: string | null;
  setPageName: (name: string | null) => void;
  updateActions: (actions: ActionProps[]) => void;
  updatePageActions: (actions: ActionProps[]) => void;
  godActions?: ReactElement | null;
  updateGodActions: (godActions: ReactElement | null) => void;
  saveStatus: SaveStatus;
  setSaveStatus: (status: SaveStatus) => void;
}

export const AppContext = React.createContext<AppContextModel>(
  {} as AppContextModel,
);

export function useApp(): AppContextModel {
  return useContext(AppContext)
}

export const AppProvider = ({ children }: AppProviderProps): ReactElement => {
  const location = useLocation();
  const appTitle = "Gratigolf";
  const pageContexts = defaultPageContexts;
  const [notices, setNotices] = useState<GratiNotice[]>([]);
  const [actions, setActions] = useState<ActionProps[]>([]);
  const [pageActions, setPageActions] = useState<ActionProps[]>([]);
  const [isHomePage, setIsHomePage] = useState<boolean>(location.pathname === "/");
  const [pageIcon, setPageIcon] = useState<typeof SvgIcon>(GratiIcon);
  const [pageName, setPageName] = useState<string | null>("Home");
  const [previousLocation, setPreviousLocation] = useState<string | null>(null);
  const [godActions, setGodActions] = useState<ReactElement | null>(null);
  const [saveStatus, setSaveStatusInternal] = useState<SaveStatus>(SAVE_STATUSES.IDLE);
  const saveStatusTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const prevLocationRef = useRef<string | null>(null);

  // Auto-reset saveStatus from 'saved' to 'idle' after 2 seconds
  const setSaveStatus = useCallback((status: SaveStatus) => {
    // Clear any existing timeout
    if (saveStatusTimeoutRef.current) {
      clearTimeout(saveStatusTimeoutRef.current);
      saveStatusTimeoutRef.current = null;
    }

    setSaveStatusInternal(status);

    // Auto-reset from 'saved' to 'idle' after 2 seconds
    if (status === SAVE_STATUSES.SAVED) {
      saveStatusTimeoutRef.current = setTimeout(() => {
        setSaveStatusInternal(SAVE_STATUSES.IDLE);
        saveStatusTimeoutRef.current = null;
      }, 2000);
    }
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveStatusTimeoutRef.current) {
        clearTimeout(saveStatusTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const urlSegments = location.pathname.split("/");
    const urlPath = urlSegments[0] + "/" + urlSegments[1];
    const pageIcon = defaultPageContexts.find((page) => page.to === urlPath)?.icon || GratiIcon;
    setPageIcon(pageIcon === HomeIcon ? GratiIcon : pageIcon);
    setIsHomePage(defaultPageContexts.find((page) => page.to === urlPath)?.isHomePage ?? false);
    prevLocationRef.current = previousLocation;
    setPreviousLocation(location.pathname);
  }, [location.pathname, previousLocation]);

  const getPageTitle = (): string => {
    if (isHomePage) {
      return appTitle;
    } else {
      const urlSegments = location.pathname.split("/");
      const urlPath = urlSegments[0] + "/" + urlSegments[1];
      const pageTitle = defaultPageContexts.find((page) => page.to === urlPath)?.name || "";
      if (pageTitle === "") {
        return pageName ? pageName : '';
      } else {
        return pageTitle;
      }

    }
  }

  const updateActions = useCallback((actions: ActionProps[]): void => {
    setActions(actions);
  }, []);

  const updatePageActions = useCallback((actions: ActionProps[]): void => {
    setPageActions(actions);
  }, []);

  const updateGodActions = useCallback((godActions: ReactElement | null): void => {
    setGodActions(godActions);
  }, []);

  const values = {
    actions,
    pageActions,
    appTitle,
    getPageTitle,
    godActions,
    isHomePage,
    notices,
    pageIcon,
    pageContexts,
    previousLocation: prevLocationRef.current,
    saveStatus,
    setActions,
    setGodActions,
    setNotices,
    setPageName,
    setSaveStatus,
    updateActions,
    updatePageActions,
    updateGodActions,
  }
  return (
    <AppContext.Provider 
      value={values}
    >
      {children}
    </AppContext.Provider>
  );
}

//export const useUserContext = (): UserContextState => {
//  return useContext(UserStateContext)
//}